import React, { useState } from "react";
import { Button, Card, Col, Row, Nav, Modal, InputGroup, Form, Alert } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useBudget } from '../hooks/useBudget';
import { BudgetForm } from '../components/Budget/BudgetForm';
import { BudgetStatsCards } from '../components/Budget/BudgetStatsCards';
import { BudgetTable } from '../components/Budget/BudgetTable';
import { BudgetHistory } from '../components/Budget/BudgetHistory';
import { AIBudgetInsights } from '../components/Budget/AIBudgetInsights';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactApexChart from "react-apexcharts";
import { useAuth } from '../utils/AuthContext';

export default function Budget() {
    const [showModal, setShowModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [expenseAmount, setExpenseAmount] = useState('');
    const [expenseDescription, setExpenseDescription] = useState('');
    const [filter, setFilter] = useState('all');
    
    const {
        budgets,
        loading: budgetLoading,
        stats: budgetStats,
        history,
        addBudget,
        updateBudget,
        deleteBudget,
        addExpense,
        clearHistory,
        calculateSpent
    } = useBudget();


    const { subscription } = useAuth();

    const loading = budgetLoading;

    // Combine stats from both hooks
    const stats = {
        ...budgetStats,
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBudget(null);
    };

    const handleCloseExpenseModal = () => {
        setShowExpenseModal(false);
        setSelectedBudget(null);
        setExpenseAmount('');
        setExpenseDescription('');
    };

    const handleClearHistory = async () => {
        if (window.confirm('Are you sure you want to clear all budget history? This action cannot be undone.')) {
            const result = await clearHistory();
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        }
    };

    const handleSubmit = async (values, formikHelpers) => {
        const submitFn = selectedBudget ? updateBudget : addBudget;
        const result = await submitFn(values, formikHelpers);
        
        if (result.success) {
            toast.success(result.message);
            handleCloseModal();
        } else {
            toast.error(result.message);
        }
    };

    const handleExpenseSubmit = async () => {
        if (!expenseAmount || isNaN(expenseAmount) || expenseAmount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        const expenseData = {
            amount: parseFloat(expenseAmount),
            description: expenseDescription,
            date: new Date().toISOString()
        };

        // Add expense to budget
        const budgetResult = await addExpense(selectedBudget.id, parseFloat(expenseAmount), expenseDescription);
        

        if (budgetResult.success) {
            toast.success('Expense added and tracked successfully');
            handleCloseExpenseModal();
        } else {
            toast.error('Failed to add expense');
        }
    };

    const filteredBudgets = budgets.filter(budget => {
        if (filter === 'all') return true;
        return budget.status === filter;
    });

    // Prepare data for category donut chart with enhanced tracking data
    const getCategoryData = () => {
        const categoryStats = stats.categoryStats || {};
        const hasData = Object.keys(categoryStats).length > 0;

        const categoryTotals = {};
        Object.entries(categoryStats).forEach(([category, data]) => {
            categoryTotals[category] = data.spent;
        });

        return {
            series: hasData ? Object.values(categoryTotals) : [1],
            options: {
                chart: {
                    type: 'donut',
                },
                labels: hasData ? Object.keys(categoryTotals) : ['No Data'],
                colors: hasData ? 
                    ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'] : 
                    ['#e9ecef'],
                legend: {
                    position: 'bottom',
                    show: hasData
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    showAlways: true,
                                    label: hasData ? 'Total' : 'No Data',
                                    formatter: function (w) {
                                        return hasData ? 
                                            `$${w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2)}` : 
                                            'No Budget Data';
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    // Prepare data for spending trend area chart with enhanced tracking data
    const getTrendData = () => {
        const monthlyStats = stats.monthlyStats || {};
        const hasData = Object.keys(monthlyStats).length > 0;

        // Sort months chronologically
        const sortedMonths = Object.keys(monthlyStats).sort((a, b) => new Date(a) - new Date(b));
        
        const series = [{
            name: 'Total Spending',
            data: hasData ? sortedMonths.map(month => monthlyStats[month].total || 0) : [0]
        }];

        if (hasData) {
            // Add category breakdown if available
            const categories = new Set();
            sortedMonths.forEach(month => {
                Object.keys(monthlyStats[month].byCategory || {}).forEach(cat => categories.add(cat));
            });

            categories.forEach(category => {
                series.push({
                    name: category,
                    data: sortedMonths.map(month => monthlyStats[month].byCategory[category] || 0)
                });
            });
        }

        return {
            series,
            options: {
                chart: {
                    type: 'area',
                    height: 350,
                    stacked: true,
                    toolbar: {
                        show: false
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                xaxis: {
                    categories: hasData ? sortedMonths.map(date => {
                        return new Date(date).toLocaleDateString('default', { month: 'short', year: 'numeric' });
                    }) : ['No Data']
                },
                colors: ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'],
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.3
                    }
                },
                tooltip: {
                    y: {
                        formatter: (value) => `$${value.toFixed(2)}`
                    }
                },
                legend: {
                    position: 'bottom',
                    horizontalAlign: 'center'
                }
            }
        };
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <Footer />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Budget Management</h4>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                    >
                        Create New Budget
                    </Button>
                </div>

                <BudgetStatsCards stats={stats} />

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <Nav className="nav-pills">
                                <Nav.Link
                                    active={filter === 'all'}
                                    onClick={() => setFilter('all')}
                                >All</Nav.Link>
                                <Nav.Link
                                    active={filter === 'active'}
                                    onClick={() => setFilter('active')}
                                >Active</Nav.Link>
                                <Nav.Link
                                    active={filter === 'over_budget'}
                                    onClick={() => setFilter('over_budget')}
                                >Over Budget</Nav.Link>
                            </Nav>
                        </div>

                        <BudgetTable 
                            budgetsWithSpent={calculateSpent()}
                            budgets={filteredBudgets}
                            onDelete={deleteBudget}
                            onEdit={(budget) => {
                                setSelectedBudget(budget);
                                setShowModal(true);
                            }}
                            onAddExpense={(budget) => {
                                setSelectedBudget(budget);
                                setShowExpenseModal(true);
                            }}
                        />
                    </Card.Body>
                </Card>

                <Row className="g-3">
                    <Col md={6}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Budget by Category</Card.Title>
                            </Card.Header>
                            <Card.Body style={{ height: '300px' }}>
                                {budgets.length === 0 ? (
                                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                        <div className="text-center">
                                            <i className="ri-pie-chart-line fs-24 mb-2"></i>
                                            <p>No budget data available</p>
                                        </div>
                                    </div>
                                ) : (
                                    <ReactApexChart
                                        options={getCategoryData().options}
                                        series={getCategoryData().series}
                                        type="donut"
                                        height={250}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Spending Trends</Card.Title>
                            </Card.Header>
                            <Card.Body style={{ height: '300px' }}>
                                {budgets.length === 0 ? (
                                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                        <div className="text-center">
                                            <i className="ri-line-chart-line fs-24 mb-2"></i>
                                            <p>No spending data available</p>
                                        </div>
                                    </div>
                                ) : (
                                    <ReactApexChart
                                        options={getTrendData().options}
                                        series={getTrendData().series}
                                        type="area"
                                        height={250}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-3 mt-4">
                    <Col md={8}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Budget History</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <BudgetHistory history={history} onClearHistory={handleClearHistory} />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <AIBudgetInsights 
                            insights={stats.insights}
                            subscription={subscription}
                        />
                    </Col>
                </Row>

                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedBudget ? 'Edit Budget' : 'Create New Budget'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <BudgetForm
                            initialValues={selectedBudget}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>

                <Modal show={showExpenseModal} onHide={handleCloseExpenseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Expense to Budget</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedBudget && (
                            <>
                                <p>Adding expense to: <strong>{selectedBudget.name}</strong></p>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Amount</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>$</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                value={expenseAmount}
                                                onChange={(e) => setExpenseAmount(e.target.value)}
                                                placeholder="Enter amount"
                                            />
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={expenseDescription}
                                            onChange={(e) => setExpenseDescription(e.target.value)}
                                            placeholder="Enter description"
                                        />
                                    </Form.Group>
                                </Form>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseExpenseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleExpenseSubmit}>
                            Add Expense
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
} 